// Label canvas: SVG ink layer (what prints) + chrome layer (selection, handles, guides, marquee) + rulers.
// Paper is always white with black ink, whatever the UI theme.
import * as barcode from '../itf/barcode.js';
import { emSize, xScale, baselineOffset, fontFamily, setMeasurer } from '../render/metrics.js';
import { S, subscribe, singleSelected, isLocked } from './state.js';
import { $, s as svgEl, h } from './dom.js';
import { icon } from './icons.js';
import { localBox, displayText, corners } from './geometry.js';
import { ppu, fitToView, renderViewChrome } from './view.js';
import { bwipReady, ensureBwip, onBwipReady } from './bwip.js';

const INK = '#000000';
const PAPER = '#ffffff';
const HANDLE_PX = 9;
const DIRS = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];

// ----- measurer -------------------------------------------------------------------------
export function installMeasurer() {
  const ctx = document.createElement('canvas').getContext('2d');
  setMeasurer((text, font) => {
    ctx.font = /\d+px/.test(font) ? font : `100px ${font}`;
    return ctx.measureText(String(text)).width;
  });
}

// ----- ink ------------------------------------------------------------------------------
function drawText(f, text) {
  const font = f.font || {};
  const box = localBox(f);
  const xs = xScale(font) || 1;
  const g = svgEl('g', { class: 'ink-text' });
  const white = f.color === 'WHITE';
  if (f.inverted) g.appendChild(svgEl('rect', { x: box.x, y: box.y, width: box.w, height: box.h, fill: white ? PAPER : INK }));
  const fill = f.inverted ? (white ? INK : PAPER) : white ? PAPER : INK;
  const rtl = !!font.rtl;
  const t = svgEl('text', {
    x: rtl ? box.w / xs : 0,
    y: 0,
    'font-size': emSize(font),
    'font-family': fontFamily(font.typeface),
    'font-weight': font.bold ? 'bold' : null,
    'font-style': font.italic ? 'italic' : null,
    'text-decoration': font.underline ? 'underline' : null,
    direction: rtl ? 'rtl' : null,
    'text-anchor': 'start',
    fill,
    style: 'dominant-baseline:alphabetic;white-space:pre',
    transform: `translate(0 ${baselineOffset(font)}) scale(${xs} 1)`,
  });
  t.textContent = text;
  g.appendChild(t);
  return g;
}

const bwipCache = new Map();
function bwipSvg(f) {
  let opts;
  try {
    if (typeof barcode.bwipOptions !== 'function') return { error: 'Barcode module not available' };
    opts = barcode.bwipOptions(f);
  } catch (err) {
    return { error: err.message || String(err) };
  }
  const key = JSON.stringify(opts);
  if (bwipCache.has(key)) return bwipCache.get(key);
  let out;
  try {
    const str = window.bwipjs.toSVG(opts);
    const vb = /viewBox="([^"]+)"/.exec(str);
    const inner = str.replace(/^[\s\S]*?<svg[^>]*>/, '').replace(/<\/svg>\s*$/, '');
    out = { viewBox: vb ? vb[1] : null, inner };
  } catch (err) {
    out = { error: String((err && err.message) || err).replace(/^bwipp\.\w+#\d+:\s*/, '') };
  }
  if (bwipCache.size > 200) bwipCache.clear();
  bwipCache.set(key, out);
  return out;
}

function placeholder(box, lines, { stroke = '#888888', fill = '#f4f4f4', color = '#555555' } = {}) {
  const g = svgEl('g');
  g.appendChild(svgEl('rect', { x: box.x, y: box.y, width: box.w, height: box.h, fill, stroke, 'stroke-width': Math.max(8, box.h / 40), 'stroke-dasharray': `${Math.max(40, box.h / 8)} ${Math.max(30, box.h / 12)}` }));
  const size = Math.min(box.h / (lines.length + 1), box.w / Math.max(8, ...lines.map((l) => l.length * 0.55)));
  lines.forEach((line, i) => {
    const t = svgEl('text', {
      x: box.x + box.w / 2,
      y: box.y + (box.h * (i + 1)) / (lines.length + 1),
      'text-anchor': 'middle',
      'font-size': size,
      'font-family': 'Arial, sans-serif',
      fill: color,
      style: 'dominant-baseline:middle',
    });
    t.textContent = line;
    g.appendChild(t);
  });
  return g;
}

function drawBarcode(f) {
  const box = localBox(f);
  if (!bwipReady()) {
    ensureBwip().catch(() => {});
    return placeholder(box, ['Loading barcode…']);
  }
  const res = bwipSvg(f);
  if (res.error || !res.viewBox) return placeholder(box, ['Invalid barcode', (res.error || 'no output').slice(0, 60)], { stroke: '#d92d20', fill: '#fff4f2', color: '#b42318' });
  // bwip renders at scale 1; map to file units with ONE uniform scale (barcode.bwipUnitsPerPx).
  const [, , pxW, pxH] = res.viewBox.split(/[\s,]+/).map(Number);
  const upp = typeof barcode.bwipUnitsPerPx === 'function' ? barcode.bwipUnitsPerPx(f) : 1;
  const spec = typeof barcode.symbologyById === 'function' ? barcode.symbologyById(f.symbology) : null;
  const quiet = f.quietMargin ?? spec?.defaults?.quietMargin;
  let x = 0;
  let y = 0;
  if (spec && spec.oneD) {
    const nbw = Number(f.narrowBarWidth) || spec.defaults.narrowBarWidth;
    const leftQuiet = !quiet ? 0 : f.symbology === 'EAN13' ? 11 : f.symbology === 'EAN8' ? 7 : 10;
    const modules = typeof barcode.linearModules === 'function' ? barcode.linearModules(f) : pxW;
    const extra = Math.max(0, pxW * upp - modules * nbw); // e.g. EAN-13 leading digit drawn left of the bars
    x = leftQuiet * nbw - (f.symbology === 'EAN13' ? extra : extra / 2);
  } else if (spec) {
    const cell = Number(f.cellWidth) || spec.defaults.cellWidth;
    x = y = !quiet ? 0 : (f.symbology === 'QR' ? 4 : 1) * cell;
  }
  const inner = svgEl('svg', { x: box.x + x, y: box.y + y, width: pxW * upp, height: pxH * upp, viewBox: res.viewBox, preserveAspectRatio: 'none', overflow: 'visible' });
  inner.innerHTML = res.inner;
  const g = svgEl('g');
  if (f.inverted) g.appendChild(svgEl('rect', { x: box.x, y: box.y, width: box.w, height: box.h, fill: INK }));
  g.appendChild(inner);
  return g;
}

function drawField(f) {
  const g = svgEl('g', { transform: `translate(${f.x || 0} ${f.y || 0}) rotate(${f.rotation || 0})`, dataset: { key: f.key } });
  if (f.kind === 'text' || f.kind === 'date') g.appendChild(drawText(f, displayText(f)));
  else if (f.kind === 'barcode') g.appendChild(drawBarcode(f));
  else g.appendChild(placeholder(localBox(f), ['Unsupported field — kept unchanged', f.name || '']));
  return g;
}

function buildInk(target, fields) {
  target.replaceChildren(...fields.map(drawField));
}

/** Standalone SVG of the printed label (white paper, no selection chrome). */
export function labelSvgString({ width, height } = {}) {
  if (!S.label) return '';
  const W = S.label.width;
  const H = S.label.height;
  const root = svgEl('svg', { xmlns: 'http://www.w3.org/2000/svg', viewBox: `0 0 ${W} ${H}`, width: width || W / 100 + 'mm', height: height || H / 100 + 'mm' });
  root.appendChild(svgEl('rect', { x: 0, y: 0, width: W, height: H, fill: PAPER }));
  const g = svgEl('g');
  buildInk(g, S.fields);
  root.appendChild(g);
  return new XMLSerializer().serializeToString(root);
}

// ----- chrome -----------------------------------------------------------------------------
const pointsOf = (f, box) => corners(f, box).map((p) => `${p.x},${p.y}`).join(' ');

function buildChrome(target) {
  const k = ppu();
  const nodes = [];
  const sel = new Set(S.selection);
  if (S.hovered && !sel.has(S.hovered)) {
    const f = S.byKey.get(S.hovered);
    if (f) nodes.push(svgEl('polygon', { class: 'field-bbox hover', points: pointsOf(f, localBox(f)) }));
  }
  for (const key of S.selection) {
    const f = S.byKey.get(key);
    if (!f) continue;
    nodes.push(svgEl('polygon', { class: 'field-bbox selected', points: pointsOf(f, localBox(f)), style: isLocked(f) ? 'stroke-dasharray:4 3' : null }));
  }
  const one = singleSelected();
  if (one && !isLocked(one) && !S.dragging) {
    const box = localBox(one);
    const hs = HANDLE_PX / k;
    const g = svgEl('g', { transform: `translate(${one.x} ${one.y}) rotate(${one.rotation || 0})` });
    const pos = {
      nw: [box.x, box.y], n: [box.x + box.w / 2, box.y], ne: [box.x + box.w, box.y], e: [box.x + box.w, box.y + box.h / 2],
      se: [box.x + box.w, box.y + box.h], s: [box.x + box.w / 2, box.y + box.h], sw: [box.x, box.y + box.h], w: [box.x, box.y + box.h / 2],
    };
    const steps = Math.round((one.rotation || 0) / 45);
    for (const dir of DIRS) {
      const [x, y] = pos[dir];
      const cursorDir = DIRS[(DIRS.indexOf(dir) + steps) % 8];
      g.appendChild(svgEl('rect', { class: `resize-handle r-${cursorDir}`, x: x - hs / 2, y: y - hs / 2, width: hs, height: hs, dataset: { handle: dir } }));
    }
    // Rotate-90° button above the top edge (printer supports 90° steps only).
    const stem = 18 / k;
    const r = 8 / k;
    const rb = svgEl('g', { class: 'rotate-90', dataset: { rotate: '90' }, style: 'cursor:pointer' });
    rb.appendChild(svgEl('line', { class: 'rotation-stem', x1: box.x + box.w / 2, y1: box.y, x2: box.x + box.w / 2, y2: box.y - stem }));
    rb.appendChild(svgEl('circle', { class: 'rotation-handle', cx: box.x + box.w / 2, cy: box.y - stem - r, r, dataset: { rotate: '90' } }));
    const arrow = svgEl('path', {
      d: `M ${box.x + box.w / 2 - r * 0.45} ${box.y - stem - r} a ${r * 0.45} ${r * 0.45} 0 1 1 ${r * 0.45} ${r * 0.45}`,
      fill: 'none', stroke: 'currentColor', 'stroke-width': 1.4, 'vector-effect': 'non-scaling-stroke', 'pointer-events': 'none', style: 'color:var(--accent)',
    });
    rb.appendChild(arrow);
    rb.appendChild(svgEl('title', { text: 'Rotate 90° (R)' }));
    g.appendChild(rb);
    nodes.push(g);
  }
  for (const gd of S.guides || []) nodes.push(svgEl('line', { class: 'smart-guide', x1: gd.x1, y1: gd.y1, x2: gd.x2, y2: gd.y2 }));
  if (S.marquee) nodes.push(svgEl('rect', { class: 'marquee', x: S.marquee.x, y: S.marquee.y, width: S.marquee.w, height: S.marquee.h }));
  target.replaceChildren(...nodes);

  const stat = $('#stat-selection');
  if (!S.selection.length) stat.textContent = '—';
  else if (one) stat.textContent = `${one.name} · ${(one.x / 100).toFixed(2)}, ${(one.y / 100).toFixed(2)} mm${one.rotation ? ` · ${one.rotation}°` : ''}`;
  else stat.textContent = `${S.selection.length} fields`;
}

// ----- rulers -------------------------------------------------------------------------------
export function renderRulers() {
  const th = $('#ruler-ticks-h');
  const tv = $('#ruler-ticks-v');
  if (!S.label || !S.showRulers) {
    th.replaceChildren();
    tv.replaceChildren();
    return;
  }
  const surf = $('#label-surface').getBoundingClientRect();
  const rh = $('#ruler-h').getBoundingClientRect();
  const rv = $('#ruler-v').getBoundingClientRect();
  const ppMm = ppu() * 100;
  const step = ppMm < 2.5 ? 20 : ppMm < 6 ? 10 : ppMm < 18 ? 5 : ppMm < 40 ? 2 : 1;
  const labelEvery = step >= 5 ? step : step === 2 ? 10 : 5;
  const mk = (horizontal, extent, origin, size) => {
    const out = [];
    for (let v = 0; v <= extent + 0.001; v += step) {
      const px = origin + v * ppMm;
      if (px < -20 || px > size + 20) continue;
      const t = h('div', { class: 'ruler-tick', style: horizontal ? { left: `${px}px` } : { top: `${px}px` } });
      if (v % labelEvery === 0) t.textContent = String(v);
      out.push(t);
    }
    return out;
  };
  th.replaceChildren(...mk(true, S.label.width / 100, surf.left - rh.left, rh.width));
  tv.replaceChildren(...mk(false, S.label.height / 100, surf.top - rv.top, rv.height));
}

// ----- align bar ------------------------------------------------------------------------------
let alignBuilt = false;
function renderAlignBar() {
  const bar = $('#align-bar');
  const show = S.selection.length >= 2 && !S.dragging;
  bar.classList.toggle('visible', show);
  if (!show) return;
  if (!alignBuilt) {
    alignBuilt = true;
    const tools = [['align-l', 'Align left edges'], ['align-cx', 'Align centres'], ['align-r', 'Align right edges'], null, ['align-t', 'Align top edges'], ['align-cy', 'Align middles'], ['align-b', 'Align bottom edges'], null, ['dist-x', 'Distribute horizontally'], ['dist-y', 'Distribute vertically']];
    for (const t of tools) {
      if (!t) {
        bar.appendChild(h('div', { class: 'bar-divider', style: { alignSelf: 'center' } }));
        continue;
      }
      bar.appendChild(
        h('button', { class: 'tool', title: t[1], 'aria-label': t[1], onClick: () => import('./actions.js').then((m) => m.alignSelection(t[0])) }, icon(t[0], '')),
      );
    }
  }
  bar.style.top = '36px';
  bar.style.left = '50%';
  bar.style.transform = 'translateX(-50%)';
}

// ----- render loop ------------------------------------------------------------------------------
const dirty = { ink: false, chrome: false, rulers: false };
let raf = 0;
export function scheduleRender(parts = { ink: true, chrome: true, rulers: true }) {
  Object.assign(dirty, Object.fromEntries(Object.entries(parts).filter(([, v]) => v)));
  if (!raf) raf = requestAnimationFrame(flush);
}

function flush() {
  raf = 0;
  const parts = { ...dirty };
  dirty.ink = dirty.chrome = dirty.rulers = false;
  const welcome = $('#welcome');
  const wrap = $('#label-wrap');
  const svg = $('#label-svg');
  if (!S.label) {
    welcome.classList.remove('hidden');
    wrap.hidden = true;
    svg.replaceChildren();
    renderRulers();
    renderViewChrome();
    return;
  }
  welcome.classList.add('hidden');
  wrap.hidden = false;
  if (S.pendingFit) {
    S.pendingFit = false;
    fitToView();
  }
  const W = S.label.width;
  const H = S.label.height;
  const k = ppu();
  const surface = $('#label-surface');
  const cssW = W * k;
  const cssH = H * k;
  surface.style.width = `${cssW}px`;
  surface.style.height = `${cssH}px`;
  const gridMm = k * 100 < 6 ? 5 : 1;
  surface.style.backgroundSize = `${gridMm * 100 * k}px ${gridMm * 100 * k}px`;
  surface.classList.toggle('grid-off', !S.showGrid);
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.setAttribute('width', String(cssW));
  svg.setAttribute('height', String(cssH));
  // Fields that run past the label edge (a printer-check error) stay visible and their handles reachable.
  svg.style.overflow = 'visible';

  let ink = svg.querySelector(':scope > g.layer-ink');
  let chrome = svg.querySelector(':scope > g.layer-chrome');
  if (!ink) {
    ink = svgEl('g', { class: 'layer-ink' });
    chrome = svgEl('g', { class: 'layer-chrome' });
    svg.replaceChildren(ink, chrome);
    parts.ink = parts.chrome = true;
  }
  if (parts.ink) buildInk(ink, S.fields);
  if (parts.ink || parts.chrome) {
    buildChrome(chrome);
    renderAlignBar();
  }

  if (S.zoomAnchor) {
    const a = S.zoomAnchor;
    S.zoomAnchor = null;
    const scroll = $('#canvas-scroll');
    const r = surface.getBoundingClientRect();
    scroll.scrollLeft += r.left + a.ux * k - a.clientX;
    scroll.scrollTop += r.top + a.uy * k - a.clientY;
  }
  renderRulers();
  renderViewChrome();
}

export function initCanvas() {
  installMeasurer();
  onBwipReady(() => {
    bwipCache.clear();
    scheduleRender({ ink: true, chrome: true });
  });
  let renderedLabel = null;
  subscribe((r) => {
    if (r.has('doc') && S.label !== renderedLabel) {
      // A newly opened / restored label is drawn immediately, not on the next animation frame.
      renderedLabel = S.label;
      scheduleRender({ ink: true, chrome: true, rulers: true });
      cancelAnimationFrame(raf);
      flush();
    } else if (r.has('doc') || r.has('view')) scheduleRender({ ink: true, chrome: true, rulers: true });
    else if (r.has('selection') || r.has('hover') || r.has('overlay')) scheduleRender({ chrome: true });
  });
  $('#canvas-scroll').addEventListener('scroll', () => scheduleRender({ rulers: true }), { passive: true });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => scheduleRender());
  scheduleRender();
}
